import {QuestionBody, QuestionTitle} from '../../../components/Layout/question';
import {Controller, useFormContext} from 'react-hook-form';
import {
  BooleanCheckbox,
  ErrorMessage,
  FormGroup,
  Input,
} from '../../../../../components/form-items';
import Select from '../../../../../components/Select/Select';
import React from 'react';
import {isNilOrEmpty} from '../../../utils/validate';
import ElasticWorkingTimeInfo from '../../../components/info-tip/elastic-working-time-info';

type ElasticWorkingTimeQuestion = {
  onChange: () => void;
};

const flexMinOptions: {label: string; value: number}[] = [
  {label: '01', value: 1},
  {label: '05', value: 5},
  {label: '10', value: 10},
  {label: '15', value: 15},
  {label: '30', value: 30},
  {label: '60', value: 60},
];

function ElasticWorkingTimeQuestionForm({
  onChange: onDataChange,
}: ElasticWorkingTimeQuestion) {
  const {
    control,
    watch,
    errors,
    formState: {isSubmitting},
  } = useFormContext();
  const isEnabled = watch('elasticWorkingTime.isEnabled');

  return (
    <QuestionBody>
      <QuestionTitle>
        Q2：請問貴公司是否有實行彈性上下班
        <ElasticWorkingTimeInfo /> ?
      </QuestionTitle>
      <Controller
        name={'elasticWorkingTime.isEnabled'}
        control={control}
        render={({onChange, value}) => (
          <FormGroup data-test-id={'enable-elastic-working-time'}>
            <BooleanCheckbox
              value={value}
              onChange={v => {
                onChange(v);
                onDataChange();
              }}
              disabled={isSubmitting}
            />
          </FormGroup>
        )}
      />
      <div
        className={
          'grid gap-4 items-center grid-cols-[auto,80px,auto,80px,auto,80px,1fr]'
        }
      >
        <span>上班時間往前彈性</span>
        <Controller
          name={'elasticWorkingTime.elasticScaleBefore'}
          render={({onChange, value}) => (
            <Input
              data-test-id={'elastic-scale-before'}
              type={'number'}
              value={value}
              disabled={!isEnabled || isSubmitting}
              onChange={e => {
                if (e.target.value === '') {
                  onChange(0);
                  return;
                }
                if (Number(e.target.value) > 540) {
                  onChange(540);
                  return;
                }
                const v = Number(e.target.value);
                if (!isNaN(v)) {
                  onChange(Math.floor(v));
                  onDataChange();
                }
              }}
            />
          )}
          rules={{
            min: {
              value: 0,
              message: '往前彈性時間不可為負數',
            },
            validate: v => {
              return isEnabled && !v && v !== 0
                ? '往前彈性分鐘為必填欄位'
                : true;
            },
          }}
        />
        <span>分鐘；上班時間往後彈性</span>
        <Controller
          name={'elasticWorkingTime.elasticScaleAfter'}
          render={({onChange, value}) => (
            <Input
              data-test-id={'elastic-scale-after'}
              type={'number'}
              value={value}
              disabled={!isEnabled || isSubmitting}
              onChange={e => {
                if (e.target.value === '') {
                  onChange(0);
                  return;
                }
                if (Number(e.target.value) > 540) {
                  onChange(540);
                  return;
                }
                const v = Number(e.target.value);
                if (!isNaN(v)) {
                  onChange(Math.floor(v));
                  onDataChange();
                }
              }}
            />
          )}
          rules={{
            min: {
              value: 0,
              message: '往後彈性時間不可為負數',
            },
            validate: v => {
              return isEnabled && !v && v !== 0
                ? '往後彈性分鐘為必填欄位'
                : true;
            },
          }}
        />
        <span>分鐘；以</span>
        <Controller
          name={'elasticWorkingTime.unitOfTime'}
          render={({onChange, value}) => (
            <div data-test-id={'unit-of-time'}>
              <Select
                options={flexMinOptions}
                isDisabled={!isEnabled || isSubmitting}
                onChange={(o: {value: string; label: string}) => {
                  onChange(o.value);
                  onDataChange();
                }}
                value={{label: value?.toString().padStart(2, '0'), value}}
              />
            </div>
          )}
          rules={{
            validate: v =>
              isEnabled && !v && v !== 0 ? '彈性刻度為必填欄位' : true,
          }}
        />
        <span>分鐘為刻度。</span>
      </div>
      <ErrorMessage>
        {(errors.elasticWorkingTime?.elasticScaleAfter ||
          errors.elasticWorkingTime?.elasticScaleBefore ||
          errors.elasticWorkingTime?.unitOfTime) &&
          [
            errors.elasticWorkingTime?.elasticScaleBefore?.message,
            errors.elasticWorkingTime?.elasticScaleAfter?.message,
            errors.elasticWorkingTime?.unitOfTime?.message,
          ]
            .filter(m => !isNilOrEmpty(m))
            .join('，')}
      </ErrorMessage>
    </QuestionBody>
  );
}

export default ElasticWorkingTimeQuestionForm;
